import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { categorySchema } from '../menu/categorySchema';
import { menuItemSchema } from '../menu/menuItemSchema';
import { zPrice, zTextNullable } from '../primitives';
import { orderItemModifierSchema } from './orderItemModifierSchema';

/**
 * Limits POS and consumer note input to 60 characters. Keep stored notes uncapped:
 * rejecting older, longer notes breaks order reads, including idle register close.
 */
export const zOrderItemNotes = z.string().max(60).nullable();

// Also stored in `ordersArchived`, which is never migrated: read `archivedOrderSchema.ts` before
// removing a field, narrowing a type, or adding a field without a `.default()`.
export const orderItemSchema = z.object({
  categoryName: categorySchema.shape.name,
  categoryUuid: z.string(), // Keep input required; categorySchema.shape.uuid has a default.
  discountPercent: z.number().nullable().default(null),
  discountTotal: z.number().default(0),
  /**
   * POS only. When the primary POS last produced a kitchen docket for this item.
   * Once set, the item must be voided instead of removed.
   *
   * Printing uses two records:
   * - Item stamps (`lastPrintedAt`, `lastPrintedQty`) record what was sent to the kitchen.
   *   The primary writes them once when saving a docket; the next docket uses only the
   *   difference between the order and its stamps.
   * - `docketSchema.printAttempts` tracks delivery, timing and printer, supporting
   *   retries, fallback printing, alerts and auditing.
   *
   * The primary's print queue persists pending dockets across restarts; a failed print is
   * rerouted to the fallback printer or kept in the failed dockets list. A docket not printed
   * within 2 hours of the save that sent its items is dropped as EXPIRED rather than printed
   * late. Stamps never wait for or change with printer results, so orders stay editable
   * and registers stay unlocked while printing.
   */
  lastPrintedAt: z.number().nullable().default(null),
  /**
   * Quantity sent to the kitchen (POS only; see `lastPrintedAt`). Set to the item's qty
   * after a NEW or ADDED docket, or `0` after a VOID docket.
   * The next docket uses `qty` minus this value. A voided item needs a VOID docket only
   * while this is above zero. Never use `lastPrintedAt` or `voidedAt` comparisons to decide.
   */
  lastPrintedQty: z.number().nullable().default(null),
  localCreatedAt: z.number().default(() => Date.now()),
  /**
   * Tracks POS item edits and the last local save, including kitchen stamp updates.
   *
   * - Cart edits update it to mark unsaved changes (qty, price, modifiers, notes, etc.).
   * - Saving sets it to the current time if it exceeds `order.localUpdatedAt`.
   * - After saving, it records when the item was persisted, like `order.localUpdatedAt`.
   *
   * Used for change detection, local save tracking and per-item sync conflict resolution.
   */
  localUpdatedAt: z.number().default(() => Date.now()),
  menuItemComesWithModifiersUuids: menuItemSchema.shape.comesWithModifiersUuids,
  menuItemDescription: menuItemSchema.shape.description,
  menuItemIsGstExempt: menuItemSchema.shape.isGstExempt,
  menuItemModifierGroupUuids: menuItemSchema.shape.modifierGroupUuids,
  menuItemName: menuItemSchema.shape.name,
  menuItemPrice: menuItemSchema.shape.price,
  menuItemSizeModifierGroupUuid: menuItemSchema.shape.sizeModifierGroupUuid,
  menuItemUuid: z.string(), // Keep input required; menuItemSchema.shape.uuid has a default.
  modifiers: z.array(orderItemModifierSchema).default([]),
  notes: z.string().nullable().default(null),
  qty: z.number().default(1),
  subtotal: z.number().default(0),
  surchargePercent: z.number().nullable().default(null),
  surchargeTotal: z.number().default(0),
  /** No default: callers must calculate the total. */
  total: z.number(),
  uuid: z.string().default(() => uuidv4()),
  /**
   * POS only. Items sent to the kitchen must be voided instead of removed.
   */
  voidedAt: z.number().nullable().default(null),
});
export type OrderItem = z.output<typeof orderItemSchema>;

/** Validates form and mutation input. */
export const orderItemStrictSchema = orderItemSchema
  .safeExtend({
    categoryUuid: z.guid(),
    discountTotal: zPrice,
    menuItemUuid: z.guid(),
    notes: zTextNullable.pipe(zOrderItemNotes),
    subtotal: zPrice,
    surchargeTotal: zPrice,
    total: zPrice,
    uuid: z.guid(),
  })
  /** Items with a size modifier group must include a modifier. */
  .superRefine((orderItem, ctx) => {
    if (orderItem.menuItemSizeModifierGroupUuid && orderItem.modifiers.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: `'${orderItem.menuItemName}' is missing a size modifier'`,
        path: ['modifiers'],
      });
    }
  });
